import React, { useEffect, useState } from 'react';
import { ref, onValue, remove, set, update } from 'firebase/database';
import { database } from '../firebase/firebaseConfig';
import {
  Button,
  Card,
  CardContent,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Title,
  Tr,
  TrH,
} from './StyledComponents';

const AdminPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    const usersRef = ref(database, 'userDirectory');
    const adminsRef = ref(database, 'admins');

    const unsubscribeUsers = onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const formattedUsers = Object.entries(data).map(([uid, value]) => ({
          uid,
          displayName: value.displayName || 'Unknown user',
          email: value.email || 'Email not stored',
          createdAt: value.createdAt || '',
          lastLogin: value.lastLogin || '',
          role: value.role || 'user',
        })).sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));

        setUsers(formattedUsers);
        setLoadingUsers(false);
      },
      (err) => {
        console.error('Failed to load users', err);
        setError('Unable to load users. Please try again.');
        setLoadingUsers(false);
      }
    );

    const unsubscribeAdmins = onValue(
      adminsRef,
      (snapshot) => {
        setAdmins(snapshot.val() || {});
      },
      (err) => {
        console.error('Failed to load admin list', err);
      }
    );

    return () => {
      unsubscribeUsers();
      unsubscribeAdmins();
    };
  }, []);

  const setProcessingFlag = (uid, value) => {
    setProcessing((prev) => {
      const next = { ...prev };
      if (value) {
        next[uid] = true;
      } else {
        delete next[uid];
      }
      return next;
    });
  };

  const updateUserRole = async (uid, displayName, role) => {
    setError('');
    setFeedback('');
    setProcessingFlag(uid, true);
    try {
      if (role === 'admin') {
        await set(ref(database, `admins/${uid}`), true);
        await update(ref(database, `userDirectory/${uid}`), { role: 'admin' });
        setFeedback(`${displayName} now has admin access.`);
      } else {
        await remove(ref(database, `admins/${uid}`));
        await update(ref(database, `userDirectory/${uid}`), { role: 'user' });
        setFeedback(`${displayName} is now a regular user.`);
      }
      setAdmins((prev) => {
        const next = { ...prev };
        if (role === 'admin') {
          next[uid] = true;
        } else {
          delete next[uid];
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to update role', err);
      setError('Could not update role. Please try again.');
    } finally {
      setProcessingFlag(uid, false);
    }
  };

  const deleteUser = async (uid, displayName) => {
    if (!window.confirm(`Delete ${displayName}? This action cannot be undone.`)) {
      return;
    }

    setError('');
    setFeedback('');
    setProcessingFlag(uid, true);
    try {
      await remove(ref(database, `users/${uid}`));
      await remove(ref(database, `userDirectory/${uid}`));
      await remove(ref(database, `admins/${uid}`));
      setFeedback(`${displayName} was deleted.`);
    } catch (err) {
      console.error('Failed to delete user', err);
      setError('Could not delete user. Please try again.');
    } finally {
      setProcessingFlag(uid, false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '–';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  };

  const renderRows = () => {
    if (!users.length) {
      return (
        <Tr style={{ cursor: 'default' }}>
          <Td colSpan={6}>No user records found.</Td>
        </Tr>
      );
    }

    return users.map(({ uid, displayName, email, createdAt, lastLogin, role }) => {
      const isCurrentUser = currentUser?.uid === uid;
      const isBusy = Boolean(processing[uid]);
      const roleValue = Object.prototype.hasOwnProperty.call(admins, uid)
        ? 'admin'
        : role;

      return (
        <Tr key={uid} style={{ cursor: 'default' }}>
          <Td>{displayName}</Td>
          <Td>{email}</Td>
          <Td>
            <Select
              value={roleValue}
              disabled={isBusy}
              onChange={(event) => updateUserRole(uid, displayName, event.target.value)}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Select>
          </Td>
          <Td>{formatDateTime(createdAt)}</Td>
          <Td>{formatDateTime(lastLogin)}</Td>
          <Td>
            <Button
              onClick={() => deleteUser(uid, displayName)}
              disabled={isBusy || isCurrentUser}
              title={isCurrentUser ? 'You cannot delete the account currently signed in.' : undefined}
            >
              Delete User
            </Button>
          </Td>
        </Tr>
      );
    });
  };

  return (
    <Card>
      <CardContent>
        <Title>Admin</Title>
        <h4>User Management</h4>
        {error && <p style={{ color: '#c26166' }}>{error}</p>}
        {feedback && <p style={{ color: '#68c174' }}>{feedback}</p>}
        {loadingUsers ? (
          <p>Loading users...</p>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <TrH>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Joined</Th>
                  <Th>Last Login</Th>
                  <Th>Actions</Th>
                </TrH>
              </Thead>
              <Tbody>{renderRows()}</Tbody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPage;
